// Static geography + threat mapping for the globe's live-weather points. Pure data,
// no fetch. Used to place NWS per-area alert counts (from /alerts/active/count, which
// the app already calls) and the SPC outlook onto the sphere.

import type { ThreatLevel } from '@/lib/criteria/levels'

/** Approx centroid [lat, lon] for the area codes NWS reports alert counts against. */
export const AREA_CENTROIDS: Record<string, [number, number]> = {
  // 50 states
  AL: [32.8, -86.8], AK: [63.6, -152.4], AZ: [34.2, -111.7], AR: [34.9, -92.4],
  CA: [37.2, -119.4], CO: [39.0, -105.5], CT: [41.6, -72.7], DE: [39.0, -75.5],
  FL: [28.6, -82.4], GA: [32.6, -83.4], HI: [20.3, -156.4], ID: [44.1, -114.7],
  IL: [40.0, -89.2], IN: [39.9, -86.3], IA: [42.0, -93.5], KS: [38.5, -98.4],
  KY: [37.5, -85.3], LA: [31.0, -92.0], ME: [45.4, -69.2], MD: [39.0, -76.7],
  MA: [42.3, -71.8], MI: [44.3, -85.4], MN: [46.3, -94.3], MS: [32.7, -89.7],
  MO: [38.4, -92.5], MT: [47.0, -109.6], NE: [41.5, -99.8], NV: [39.3, -116.6],
  NH: [43.7, -71.6], NJ: [40.2, -74.7], NM: [34.4, -106.1], NY: [42.9, -75.5],
  NC: [35.5, -79.4], ND: [47.4, -100.5], OH: [40.3, -82.8], OK: [35.6, -97.5],
  OR: [43.9, -120.6], PA: [40.9, -77.8], RI: [41.7, -71.6], SC: [33.9, -80.9],
  SD: [44.4, -100.2], TN: [35.9, -86.4], TX: [31.5, -99.3], UT: [39.3, -111.7],
  VT: [44.1, -72.7], VA: [37.5, -78.9], WA: [47.4, -120.5], WV: [38.6, -80.6],
  WI: [44.6, -89.9], WY: [43.0, -107.6],
  // DC + territories + freely-associated states NWS issues for
  DC: [38.9, -77.0], PR: [18.2, -66.4], VI: [18.0, -64.8], GU: [13.4, 144.8],
  AS: [-14.3, -170.7], MP: [15.2, 145.7], FM: [6.9, 158.2], MH: [7.1, 171.2],
  PW: [7.5, 134.6],
  // Marine zone groupings — coarse anchors so offshore alerts still register
  AN: [38.0, -71.0], AM: [27.0, -76.0], GM: [26.0, -90.0], PZ: [34.0, -122.0],
  PK: [57.0, -150.0], PH: [22.0, -158.0], LE: [42.2, -81.2], LO: [43.6, -77.8],
  LH: [44.5, -82.4], LM: [43.5, -87.0], LS: [47.5, -88.0], SL: [44.5, -75.8],
}

/** Centre of the contiguous US — where the SPC categorical outlook is anchored. */
export const CONUS_CENTROID: [number, number] = [39.0, -97.5]

/**
 * Pseudo-intensity for a state's active-alert tally. We only have counts from
 * /alerts/active/count (not per-alert severity), so density stands in for the
 * calm→intense ramp. Labels stay honest ("N active alerts").
 */
export function alertCountLevel(count: number): ThreatLevel {
  if (count >= 8) return 'severe'
  if (count >= 4) return 'warning'
  if (count >= 2) return 'watch'
  return 'info'
}

/** Tropical system intensity → threat ramp. */
export function tropicalLevel(category?: number, classification?: string): ThreatLevel {
  if (typeof category === 'number') {
    if (category >= 5) return 'extreme'
    if (category >= 3) return 'severe'
    if (category >= 1) return 'warning'
  }
  const c = (classification ?? '').toLowerCase()
  if (c.includes('hurricane') || c.includes('typhoon') || c.includes('cyclone')) return 'severe'
  if (c.includes('tropical storm')) return 'watch'
  if (c.includes('depression')) return 'info'
  return 'info' // invest / disturbance / potential
}
